import { Anchor, Box, Button, Drawer, Skeleton, Text, Title } from "@mantine/core";
import { PaperPlaneTiltIcon, WalletIcon } from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { ARApi, NetworkApi } from "../../apis";
import { AgingChart, type AgingBucketKey } from "../../components/ar/aging-chart";
import { ARMetricsCards, type OldestInvoice } from "../../components/ar/ar-metrics-cards";
import { ARStatementTable } from "../../components/ar/ar-statement-table";
import {
  CustomerInvoiceTable,
  type InvoiceSeg,
} from "../../components/ar/customer-invoice-table";
import { CreditControlCard } from "../../components/ar/credit-control-card";
import { PaymentBehaviorChart } from "../../components/ar/payment-behavior-chart";
import { ReminderDrawer } from "../../components/invoice-actions/reminder-drawer";
import { Page } from "../../components/layout/Page";
import { Breadcrumb } from "../../components/layout/breadcrumb";
import {
  RegisterPayment,
  REGISTER_PAYMENT_DRAWER_STYLES,
} from "../../components/register-payment/register-payment";
import { usePermission } from "../../components/permission";
import { IGeneral } from "../../interfaces/IGeneral";
import { paymentTermLabel } from "../../utils/payment-term-label";
import { renderBusinessName } from "../../utils/render-business-name";
import { renderName } from "../../utils/render-name";
import classes from "./BillingDetails.module.css";
import { ReasonTooltip } from "../../components/ui/reason-tooltip";

/** Statement download has no handler — hide until product wires it. */
const SHOW_STATEMENT_DOWNLOAD = false;

type MainTab = "invoices" | "statement" | "behavior";

type DetailQuery = {
  tab: MainTab;
  seg: InvoiceSeg;
  bucket: AgingBucketKey | null;
  page: number;
  limit: number;
  q: string;
};

const DEFAULT_LIMIT = 40;
const BUCKETS: AgingBucketKey[] = ["NOT_DUE", "D1_30", "D31_60", "D61_90", "D90_PLUS"];

function parseTab(raw: string | null): MainTab {
  if (raw === "statement" || raw === "behavior" || raw === "invoices") return raw;
  return "invoices";
}

function parseSeg(raw: string | null): InvoiceSeg {
  if (raw === "OVERDUE" || raw === "PENDING" || raw === "CLOSED" || raw === "OPEN") return raw;
  return "OPEN";
}

function parseBucket(raw: string | null): AgingBucketKey | null {
  if (raw && (BUCKETS as string[]).includes(raw)) return raw as AgingBucketKey;
  return null;
}

function readQuery(params: URLSearchParams): DetailQuery {
  return {
    tab: parseTab(params.get("tab")),
    seg: parseSeg(params.get("seg")),
    bucket: parseBucket(params.get("bucket")),
    page: Math.max(1, parseInt(params.get("page") || "1", 10) || 1),
    limit: Math.max(1, parseInt(params.get("limit") || String(DEFAULT_LIMIT), 10) || DEFAULT_LIMIT),
    q: params.get("q") || "",
  };
}

function writeQuery(q: DetailQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.tab !== "invoices") p.set("tab", q.tab);
  if (q.seg !== "OPEN") p.set("seg", q.seg);
  if (q.bucket) p.set("bucket", q.bucket);
  if (q.page !== 1) p.set("page", String(q.page));
  if (q.limit !== DEFAULT_LIMIT) p.set("limit", String(q.limit));
  if (q.q) p.set("q", q.q);
  return p;
}

function bankAccountLabel(account: { name?: string; bankName?: string; number?: string } | null | undefined): string {
  if (!account) return "";
  const bank = account.name || account.bankName || "";
  const number = account.number || "";
  if (bank && number) return `${bank} · ${number}`;
  return bank || number;
}

export const BillingDetails = () => {
  const { t } = useTranslation(["billing", "common", "invoice"]);
  const params = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navCustomer = (location.state as any) || {};
  const listReturn = navCustomer.listQuery
    ? `/customer-billing?${navCustomer.listQuery}`
    : "/customer-billing";

  const { paymentTermConditions } = useSelector((state: { general: IGeneral }) => state.general);
  const INV_REG_PAYMENT = usePermission("INV_REG_PAYMENT");

  const q = useMemo(() => readQuery(searchParams), [searchParams]);

  const patchQuery = useCallback(
    (patch: Partial<DetailQuery>, resetPage = true) => {
      const next: DetailQuery = {
        ...q,
        ...patch,
        page: resetPage && patch.page === undefined ? 1 : (patch.page ?? q.page),
      };
      setSearchParams(writeQuery(next), { replace: true });
    },
    [q, setSearchParams],
  );

  const businessId = params.id!;
  const { data: arSummary } = useSwr(
    businessId ? `ar.summary.${businessId}` : null,
    async () => ARApi.getSummary(businessId),
    { revalidateOnFocus: false },
  );

  const {
    data: network,
    error: networkError,
    isLoading: networkLoading,
  } = useSwr(
    businessId ? `inv.network.${businessId}` : null,
    async () => NetworkApi.get(businessId),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  /** API wins when present; nav state is load placeholder / error fallback. */
  const customer = network ?? navCustomer;
  const networkPending = networkLoading && !network && !networkError;
  const navHasMeta = !!(
    navCustomer.regNumber ||
    navCustomer.finStaff ||
    navCustomer.staff ||
    navCustomer.paymentTerm ||
    navCustomer.paymentTermObject ||
    navCustomer.partner?.businessAddress ||
    navCustomer.bankAccount
  );
  const showMetaSkeleton = networkPending && !navHasMeta;
  const showContactSkeleton = networkPending && !navHasMeta;

  const [oldest, setOldest] = useState<OldestInvoice>(null);
  const [earliestOpenDate, setEarliestOpenDate] = useState<string | null>(null);
  const [firstOverdueId, setFirstOverdueId] = useState<string | null>(null);
  const [reminderOpened, setReminderOpened] = useState(false);
  const [action, setAction] = useState<any[]>([]);

  const name = renderBusinessName(customer, t("billing:detail.buyer"));
  const refCode = customer.refCode ?? "";
  const regNumber = customer.regNumber ?? "";
  const staff = customer.staff ?? customer.finStaff ?? null;
  const finStaffName = staff ? renderName(staff) : "";
  const paymentTermName = paymentTermLabel(
    customer?.paymentTerm ?? customer?.paymentTermObject ?? null,
    paymentTermConditions,
    "-",
  );

  const overdueCount = arSummary?.metrics?.overdueInvoicesCount ?? 0;
  const onCreditHold = !!arSummary?.creditHold?.onCreditHold;

  const statusPill = onCreditHold
    ? t("billing:creditHold.onHold")
    : overdueCount > 0
      ? t("billing:detail.overduePill", { count: overdueCount })
      : null;

  const contactPhone = (staff?.phone as string | undefined)?.trim() || "";
  const contactEmail = (staff?.email as string | undefined)?.trim() || "";
  const contactAddress =
    customer.partner?.businessAddress || customer.address || customer.partner?.address || "";
  const contactAccount = bankAccountLabel(customer.bankAccount);
  const hasContact = !!(finStaffName || contactPhone || contactEmail || contactAddress || contactAccount);

  const mainTabs: { value: MainTab; label: string }[] = [
    {
      value: "invoices",
      label: t("billing:detail.tabInvoices", {
        count: arSummary?.metrics?.openInvoicesCount ?? "",
      }),
    },
    { value: "statement", label: t("billing:tab.statement") },
    { value: "behavior", label: t("billing:tab.behavior") },
  ];

  return (
    <Page>
      <Box className={classes.pageHdr}>
        <Box className={classes.pageHdrLeft}>
          <Breadcrumb data={[
              { label: t("billing:breadcrumb.list"), to: listReturn },
              { label: name || "…" },
            ]} />

          <Box className={classes.titleRow}>
            <Title order={1} className={classes.pageTitle}>
              {networkPending && !name ? <Skeleton h={28} w={220} /> : name}
            </Title>
            {statusPill && (
              <Text
                component="span"
                className={`${classes.statusPill} ${onCreditHold ? classes.statusPillHold : ""}`}
              >
                {statusPill}
              </Text>
            )}
          </Box>

          {showMetaSkeleton ? (
            <Skeleton h={16} w={360} mt={4} />
          ) : (
            <Text className={classes.pageSub}>
              {refCode ? (
                <Text component="span" className={classes.pageSubMono}>
                  {refCode}
                </Text>
              ) : null}
              {refCode && regNumber ? " · " : null}
              {regNumber ? (
                <>
                  {t("invoice:detail.meta.ttd")}{" "}
                  <Text component="span" className={classes.pageSubMono}>
                    {regNumber}
                  </Text>
                </>
              ) : null}
              {finStaffName ? ` · ${t("billing:col.financeStaff")} ${finStaffName}` : null}
              {paymentTermName ? ` · ${t("billing:detail.terms")}: ${paymentTermName}` : null}
            </Text>
          )}
        </Box>

        <Box className={classes.pageHdrRight}>
          {SHOW_STATEMENT_DOWNLOAD && (
            <Button variant="default" classNames={{ root: classes.actionBtn }}>
              {t("billing:detail.downloadStatement")}
            </Button>
          )}
          {firstOverdueId && (
            <Button
              variant="default"
              classNames={{ root: classes.actionBtn }}
              leftSection={<PaperPlaneTiltIcon size={14} />}
              onClick={() => setReminderOpened(true)}
            >
              {t("billing:actions.remind")}
            </Button>
          )}
            <ReasonTooltip reason={INV_REG_PAYMENT.reason}>
              <Button
                color="brand"
                classNames={{ root: classes.actionBtnPrimary }}
                leftSection={<WalletIcon size={14} weight="bold" />}
                onClick={() => setAction(["payment_register"])}
              >
                {t("invoice:detail.summaryCard.registerPay")}
              </Button>
            </ReasonTooltip>
        </Box>
      </Box>

      <ARMetricsCards metrics={arSummary?.metrics || null} oldest={oldest} />

      <AgingChart
        aging={arSummary?.aging || null}
        activeBucket={q.bucket}
        onBucketChange={(bucket) => {
          patchQuery({
            bucket,
            tab: "invoices",
            seg: bucket && bucket !== "NOT_DUE" ? "OVERDUE" : q.seg === "CLOSED" || q.seg === "PENDING" ? "OPEN" : q.seg,
          });
        }}
      />

      <Box className={classes.grid}>
        <Box className={classes.colMain}>
          <Box className={classes.card}>
            <Box className={classes.underlineTabs} role="tablist">
              {mainTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={q.tab === tab.value}
                  className={`${classes.underlineTab} ${q.tab === tab.value ? classes.underlineTabActive : ""}`}
                  onClick={() => patchQuery({ tab: tab.value }, false)}
                >
                  {tab.value === "invoices"
                    ? t("billing:detail.tabInvoices", {
                        count: arSummary?.metrics?.openInvoicesCount ?? 0,
                      })
                    : tab.label}
                </button>
              ))}
            </Box>

            <Box className={classes.tabBody}>
              {q.tab === "invoices" && (
                <CustomerInvoiceTable
                  businessId={businessId}
                  seg={q.seg}
                  bucket={q.bucket}
                  page={q.page}
                  pageSize={q.limit}
                  q={q.q}
                  onSegChange={(seg) =>
                    patchQuery({
                      seg,
                      bucket: seg === "PENDING" || seg === "CLOSED" ? null : q.bucket,
                    })
                  }
                  onBucketClear={() => patchQuery({ bucket: null })}
                  onPageChange={(page) => patchQuery({ page }, false)}
                  onPageSizeChange={(limit) => patchQuery({ limit, page: 1 })}
                  onSearchChange={(nextQ) => patchQuery({ q: nextQ })}
                  onOldestLoaded={setOldest}
                  onEarliestOpenDate={setEarliestOpenDate}
                  onFirstOverdueId={setFirstOverdueId}
                />
              )}
              {q.tab === "statement" && (
                <ARStatementTable businessId={businessId} earliestOpenDate={earliestOpenDate} />
              )}
              {q.tab === "behavior" && (
                <PaymentBehaviorChart
                  paymentTrend={arSummary?.paymentTrend || null}
                  latePaymentTrend={arSummary?.latePaymentTrend || null}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box className={classes.colSide}>
          <CreditControlCard creditHold={arSummary?.creditHold || null} />

          <Box className={`${classes.card} ${classes.cardPad}`}>
            <Text className={classes.cardTitle}>{t("billing:detail.contactTitle")}</Text>
            {showContactSkeleton ? (
              <>
                <Skeleton h={12} w="40%" mb={8} />
                <Skeleton h={14} w="70%" mb={12} />
                <Skeleton h={12} w="35%" mb={8} />
                <Skeleton h={14} w="85%" />
              </>
            ) : hasContact ? (
              <>
                {finStaffName && (
                  <Box className={classes.contactLine}>
                    <Text className={classes.contactLabel}>{t("billing:col.financeStaff")}</Text>
                    <Text className={classes.contactValue}>{finStaffName}</Text>
                  </Box>
                )}
                {contactPhone && (
                  <Box className={classes.contactLine}>
                    <Text className={classes.contactLabel}>{t("billing:detail.contactPhone")}</Text>
                    <Anchor
                      href={`tel:${contactPhone.replace(/\s+/g, "")}`}
                      className={`${classes.contactValue} ${classes.contactPhone} ${classes.contactLink}`}
                      underline="hover"
                    >
                      {contactPhone}
                    </Anchor>
                  </Box>
                )}
                {contactEmail && (
                  <Box className={classes.contactLine}>
                    <Text className={classes.contactLabel}>{t("billing:detail.contactEmail")}</Text>
                    <Anchor
                      href={`mailto:${contactEmail}`}
                      className={`${classes.contactValue} ${classes.contactEmail} ${classes.contactLink}`}
                      underline="hover"
                      title={contactEmail}
                    >
                      {contactEmail}
                    </Anchor>
                  </Box>
                )}
                {contactAddress && (
                  <Box className={classes.contactLine}>
                    <Text className={classes.contactLabel}>{t("billing:detail.contactAddress")}</Text>
                    <Text className={classes.contactValue}>{contactAddress}</Text>
                  </Box>
                )}
                {contactAccount && (
                  <Box className={classes.contactLine}>
                    <Text className={classes.contactLabel}>{t("billing:detail.contactAccount")}</Text>
                    <Text className={classes.contactValue}>{contactAccount}</Text>
                  </Box>
                )}
              </>
            ) : (
              <Text c="dimmed" size="sm">
                {t("billing:panel.empty")}
              </Text>
            )}
          </Box>
        </Box>
      </Box>

      {firstOverdueId && (
        <ReminderDrawer
          invoiceId={firstOverdueId}
          opened={reminderOpened}
          onClose={() => setReminderOpened(false)}
        />
      )}

      <Drawer
        opened={action[0] === "payment_register"}
        onClose={() => setAction([])}
        position="right"
        size="xl"
        styles={REGISTER_PAYMENT_DRAWER_STYLES}
        title={null}
        withCloseButton={false}
        padding={0}
      >
        <RegisterPayment
          action={action}
          setAction={setAction}
          reload={async () => undefined}
          prefillBusinessId={businessId}
        />
      </Drawer>
    </Page>
  );
};
